import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Loader2, ClipboardCheck, Check, X, RefreshCw, Upload, FileText, Clock, AlertTriangle, CheckCircle2, Hourglass, Download } from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { fmtDateTime } from "@/lib/datetime";

const th = "font-display font-bold px-4 py-3 text-xs tracking-wider uppercase text-left text-[#52525B]";
const fmt = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(n || 0));
const fmtFecha = (iso) => fmtDateTime(iso);

const ESTADO_STYLE = {
  Aprobada: "text-[#047857] border-[#10B981]/40 bg-[#10B981]/5",
  Pendiente: "text-[#B45309] border-[#F59E0B]/40 bg-[#F59E0B]/5",
  Rechazada: "text-[#B91C1C] border-[#EF4444]/40 bg-[#EF4444]/5",
  Expirada: "text-[#52525B] border-[#a1a1aa]/40 bg-[#a1a1aa]/10",
};
const FILTROS = ["Pendiente", "Aprobada", "Rechazada", "Expirada", "Todos"];
const PAGE_SIZE = 25;

export default function ValidacionRecargas() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("Pendiente");
  const [accion, setAccion] = useState(null); // { recarga, tipo: 'Aprobada'|'Rechazada', motivo }
  const [saving, setSaving] = useState(false);
  const [upload, setUpload] = useState(null); // { recarga, pdf, xml }
  const [uploading, setUploading] = useState(false);
  const [errorModal, setErrorModal] = useState(null); // { recarga, nota }
  const [savingError, setSavingError] = useState(false);
  const [resumen, setResumen] = useState(null);
  const [cfdiFiltro, setCfdiFiltro] = useState(null); // 'Pendiente' | 'Error' | 'Timbrado' | null

  const loadResumen = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/cfdi/resumen");
      setResumen(data);
    } catch (err) { /* KPIs no críticos */ }
  }, []);

  const load = useCallback(async (f, cfdi, p = 0) => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: p * PAGE_SIZE };
      if (cfdi) {
        params.cfdi_estado = cfdi;
      } else {
        if (f && f !== "Todos") params.estado = f;
        // La pestaña PENDIENTE solo lista transferencias (las tarjetas se auto-aprueban por webhook).
        if (f === "Pendiente") params.metodo = "Transferencia";
      }
      const { data } = await api.get("/admin/recargas", { params });
      setRows(data.items);
      setTotal(data.total);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { setPage(0); load(filtro, cfdiFiltro, 0); }, [filtro, cfdiFiltro, load]);
  useEffect(() => { loadResumen(); }, [loadResumen]);

  const cambiarPagina = (p) => { setPage(p); load(filtro, cfdiFiltro, p); };
  const filtrarPorCfdi = (estado) => { setCfdiFiltro(estado); setFiltro("Todos"); };
  const seleccionarFiltro = (f) => { setCfdiFiltro(null); setFiltro(f); };

  const exportarCsv = async () => {
    try {
      const params = {};
      if (cfdiFiltro) { params.cfdi_estado = cfdiFiltro; }
      else {
        if (filtro !== "Todos") params.estado = filtro;
        if (filtro === "Pendiente") params.metodo = "Transferencia";
      }
      const resp = await api.get("/admin/recargas/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `recargas_cfdi_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Exportación generada.");
    } catch (err) { toast.error("No se pudo exportar el CSV."); }
  };
  const visibleRows = rows;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const confirmar = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/recargas/${accion.recarga.id_recarga}/estado`, {
        estado: accion.tipo,
        motivo: accion.motivo?.trim() || null,
      });
      toast.success(accion.tipo === "Aprobada" ? "Recarga aprobada. Folios acreditados a la sucursal." : "Recarga rechazada.");
      setAccion(null);
      load(filtro, cfdiFiltro, page);
      window.dispatchEvent(new Event("recargas:refresh"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const subirFactura = async () => {
    toast.dismiss();
    if (!upload.pdf || !upload.xml) { toast.error("Selecciona el PDF y el XML."); return; }
    if (!upload.pdf.name.toLowerCase().endsWith(".pdf")) { toast.error("El primer archivo debe ser .pdf"); return; }
    if (!upload.xml.name.toLowerCase().endsWith(".xml")) { toast.error("El segundo archivo debe ser .xml"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("pdf", upload.pdf);
      fd.append("xml", upload.xml);
      await api.post(`/admin/cfdi/${upload.recarga.cfdi_id}/subir-archivos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Factura cargada. El comprobante quedó Timbrado.");
      setUpload(null);
      load(filtro, cfdiFiltro, page);
      loadResumen();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const marcarError = async () => {
    if (!errorModal.nota?.trim()) { toast.error("Escribe el motivo del error."); return; }
    setSavingError(true);
    try {
      await api.patch(`/admin/cfdi/${errorModal.recarga.cfdi_id}/error`, { nota_error: errorModal.nota.trim() });
      toast.success("Comprobante marcado como Error. El tenant podrá corregir y reintentar.");
      setErrorModal(null);
      load(filtro, cfdiFiltro, page);
      loadResumen();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingError(false); }
  };

  return (
    <DashboardLayout>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 flex items-center gap-2"><ClipboardCheck className="h-4 w-4" /> Tesorería</p>
            <h1 className="font-display text-4xl font-black tracking-tight">Validación de Recargas</h1>
            <p className="text-sm text-[#52525B] mt-2">Valida las transferencias y acredita los folios a la sucursal del tenant.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={exportarCsv} data-testid="export-csv" className="h-11 px-5 border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm inline-flex items-center gap-2 hover:bg-[#0A0A0A] hover:text-white transition-colors duration-200"><Download className="h-4 w-4" /> Exportar CSV</button>
            <button onClick={() => { load(filtro, cfdiFiltro, page); loadResumen(); }} data-testid="recargas-refresh" className="h-11 px-5 border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm inline-flex items-center gap-2"><RefreshCw className="h-4 w-4" /> Actualizar</button>
          </div>
        </div>

        {/* Tablero: Estado de Facturación (CFDI) */}
        {resumen && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="cfdi-resumen">
            {[
              { key: "pendientes", label: "CFDI Pendientes", value: resumen.pendientes, Icon: Hourglass, cls: "text-[#B45309]", bar: "bg-[#F59E0B]", ring: "ring-[#F59E0B]", filtro: "Pendiente" },
              { key: "error", label: "En Error", value: resumen.error, Icon: AlertTriangle, cls: "text-[#B91C1C]", bar: "bg-[#EF4444]", ring: "ring-[#EF4444]", filtro: "Error" },
              { key: "timbrados_mes", label: "Timbrados (mes)", value: resumen.timbrados_mes, Icon: CheckCircle2, cls: "text-[#047857]", bar: "bg-[#10B981]", ring: "ring-[#10B981]", filtro: "Timbrado" },
              { key: "antiguedad", label: "Antigüedad máx. (días)", value: resumen.antiguedad_max_dias, Icon: Clock, cls: "text-[#0A0A0A]", bar: "bg-[#0A0A0A]", ring: "", filtro: null },
            ].map(({ key, label, value, Icon, cls, bar, ring, filtro: fCfdi }) => {
              const clickable = !!fCfdi;
              const active = clickable && cfdiFiltro === fCfdi;
              return (
                <button key={key} type="button" onClick={() => clickable && (active ? setCfdiFiltro(null) : filtrarPorCfdi(fCfdi))}
                  disabled={!clickable}
                  className={`relative text-left bg-white border p-4 overflow-hidden transition-all duration-200 ${clickable ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-md" : "cursor-default"} ${active ? `border-[#0A0A0A] ring-2 ${ring}` : "border-[#e4e4e7]"}`}
                  data-testid={`kpi-${key}`}>
                  <div className={`absolute left-0 top-0 h-full w-1 ${bar}`} />
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#52525B]">{label}</p>
                    <Icon className={`h-4 w-4 ${cls}`} strokeWidth={2.2} />
                  </div>
                  <p className={`font-display text-4xl font-black mt-2 ${cls}`}>{value}</p>
                  {clickable && <p className="text-[10px] font-bold uppercase tracking-wider text-[#a1a1aa] mt-1">{active ? "Filtrando · quitar" : "Ver en lista"}</p>}
                </button>
              );
            })}
          </div>
        )}

        {/* Pendientes más antiguos (priorización) */}
        {resumen?.pendientes_detalle?.length > 0 && (
          <div className="mb-8 border border-[#F59E0B]/40 bg-[#F59E0B]/5 p-4" data-testid="cfdi-pendientes-detalle">
            <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#B45309] mb-2 flex items-center gap-1.5"><Hourglass className="h-3.5 w-3.5" /> Solicitudes de factura por atender</p>
            <ul className="space-y-1 text-sm">
              {resumen.pendientes_detalle.map((p) => (
                <li key={p.cfdi_id} className="flex items-center justify-between gap-3">
                  <span className="truncate"><b>{p.empresa_nombre || "—"}</b> · {fmt(p.monto_pagado)}</span>
                  <span className="text-[#52525B] whitespace-nowrap">{p.dias === 0 ? "hoy" : `hace ${p.dias} día(s)`}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Filtro por estado */}
        <div className="flex gap-2 mb-4 flex-wrap items-center" data-testid="recargas-filtro">
          {FILTROS.map((f) => (
            <button key={f} onClick={() => seleccionarFiltro(f)} data-testid={`filtro-${f}`}
              className={`h-9 px-4 border text-xs font-bold uppercase tracking-wider transition-colors duration-200 ${!cfdiFiltro && filtro === f ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#e4e4e7] hover:border-[#0A0A0A]"}`}>
              {f}
            </button>
          ))}
          {cfdiFiltro && (
            <button onClick={() => setCfdiFiltro(null)} data-testid="cfdi-filtro-chip"
              className="h-9 px-4 border border-[#0A0A0A] bg-[#FFCC00] text-[#0A0A0A] text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5">
              CFDI: {cfdiFiltro} <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <div className="bg-white border border-[#e4e4e7] overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-recargas-table">
            <thead><tr className="border-b border-[#e4e4e7]"><th className={th}>Fecha</th><th className={th}>Empresa</th><th className={th}>Sucursal</th><th className={th}>Paquete</th><th className={`${th} text-right`}>Folios</th><th className={`${th} text-right`}>Monto</th><th className={th}>Método</th><th className={th}>Referencia</th><th className={th}>Estado</th><th className={`${th} text-right`}>Acciones</th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={10} className="px-4 py-16 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></td></tr>
              : visibleRows.length === 0 ? <tr><td colSpan={10} className="px-4 py-12 text-center text-[#52525B]" data-testid="admin-recargas-empty">{filtro === "Pendiente" ? "No hay transferencias por validar." : "Sin recargas para este filtro."}</td></tr>
              : visibleRows.map((r) => (
                <tr key={r.id_recarga} className="border-b border-[#f0f0f1]" data-testid={`admin-recarga-row-${r.id_recarga}`}>
                  <td className="px-4 py-3 whitespace-nowrap">{fmtFecha(r.fecha_creacion)}</td>
                  <td className="px-4 py-3 font-semibold">{r.empresa_nombre || "—"}</td>
                  <td className="px-4 py-3">{r.sucursal_nombre || <span className="text-[#B91C1C]">Sin sucursal</span>}</td>
                  <td className="px-4 py-3">{r.paquete_nombre || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono">{r.cantidad_folios.toLocaleString("es-MX")}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold">{fmt(r.monto_pagado)}</td>
                  <td className="px-4 py-3">{r.metodo_pago}</td>
                  <td className="px-4 py-3 text-[#52525B]">{r.referencia_comprobante || "—"}</td>
                  <td className="px-4 py-3"><span className={`inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border ${ESTADO_STYLE[r.estado] || ""}`} data-testid={`admin-recarga-estado-${r.id_recarga}`}>{r.estado}</span></td>
                  <td className="px-4 py-3">
                    {r.estado === "Pendiente" ? (
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setAccion({ recarga: r, tipo: "Aprobada", motivo: "" })} data-testid={`aprobar-${r.id_recarga}`} title="Aprobar" className="h-8 w-8 inline-flex items-center justify-center border border-[#e4e4e7] hover:border-[#047857] text-[#047857] transition-colors duration-200"><Check className="h-4 w-4" /></button>
                        <button onClick={() => setAccion({ recarga: r, tipo: "Rechazada", motivo: "" })} data-testid={`rechazar-${r.id_recarga}`} title="Rechazar" className="h-8 w-8 inline-flex items-center justify-center border border-[#e4e4e7] hover:border-[#B91C1C] text-[#B91C1C] transition-colors duration-200"><X className="h-4 w-4" /></button>
                      </div>
                    ) : r.cfdi_estado === "Pendiente" ? (
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setUpload({ recarga: r, pdf: null, xml: null })} data-testid={`subir-factura-${r.id_recarga}`}
                          className="h-8 px-3 border border-[#0A0A0A] font-bold uppercase tracking-wider text-[11px] inline-flex items-center gap-1.5 hover:bg-[#0A0A0A] hover:text-white transition-colors duration-200">
                          <Upload className="h-3.5 w-3.5" /> Subir Factura
                        </button>
                        <button onClick={() => setErrorModal({ recarga: r, nota: "" })} data-testid={`marcar-error-${r.id_recarga}`} title="Marcar Error"
                          className="h-8 px-3 border border-[#B91C1C] text-[#B91C1C] font-bold uppercase tracking-wider text-[11px] inline-flex items-center gap-1.5 hover:bg-[#B91C1C] hover:text-white transition-colors duration-200">
                          <X className="h-3.5 w-3.5" /> Marcar Error
                        </button>
                      </div>
                    ) : r.cfdi_estado === "Timbrado" ? (
                      <span className="flex justify-end text-[10px] uppercase tracking-wider font-bold text-[#047857]" data-testid={`cfdi-timbrado-${r.id_recarga}`}>Timbrado ✓</span>
                    ) : r.cfdi_estado === "Error" ? (
                      <span className="flex justify-end text-[10px] uppercase tracking-wider font-bold text-[#B91C1C]" data-testid={`cfdi-error-${r.id_recarga}`}>Error</span>
                    ) : <span className="text-[#a1a1aa] text-xs flex justify-end">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Paginación */}
        {!loading && total > 0 && (
          <div className="flex items-center justify-between mt-4 text-sm" data-testid="recargas-paginacion">
            <p className="text-[#52525B]">Mostrando <b>{rows.length}</b> de <b>{total}</b> · Página {page + 1} de {totalPages}</p>
            <div className="flex gap-2">
              <button onClick={() => cambiarPagina(page - 1)} disabled={page === 0} data-testid="pag-anterior" className="h-9 px-4 border border-[#0A0A0A] font-bold uppercase tracking-wider text-xs disabled:opacity-40">Anterior</button>
              <button onClick={() => cambiarPagina(page + 1)} disabled={page + 1 >= totalPages} data-testid="pag-siguiente" className="h-9 px-4 border border-[#0A0A0A] font-bold uppercase tracking-wider text-xs disabled:opacity-40">Siguiente</button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Modal de confirmación */}
      <Dialog open={accion !== null} onOpenChange={(o) => { if (!o) setAccion(null); }}>
        <DialogContent className="rounded-none border-[#0A0A0A]" data-testid="accion-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-black tracking-tight">{accion?.tipo === "Aprobada" ? "Aprobar recarga" : "Rechazar recarga"}</DialogTitle>
            <DialogDescription>
              {accion?.tipo === "Aprobada"
                ? (accion?.recarga?.sucursal_nombre
                    ? `Se acreditarán ${accion?.recarga?.cantidad_folios?.toLocaleString("es-MX")} folios a "${accion?.recarga?.sucursal_nombre}" (${accion?.recarga?.empresa_nombre}).`
                    : `Al aprobar esta recarga, los folios serán acreditados a la bolsa global del Tenant.`)
                : `La recarga de "${accion?.recarga?.empresa_nombre}" será rechazada. No se acreditarán folios.`}
            </DialogDescription>
          </DialogHeader>
          {accion && (
            <div className="space-y-4 py-2">
              <div className="border border-[#e4e4e7] p-4 flex justify-between items-center">
                <div><p className="text-xs uppercase tracking-wider font-bold text-[#52525B]">Paquete</p><p className="font-display font-black">{accion.recarga.paquete_nombre || "—"}</p></div>
                <div className="text-right"><p className="text-xs uppercase tracking-wider font-bold text-[#52525B]">Monto</p><p className="font-mono font-black">{fmt(accion.recarga.monto_pagado)}</p></div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Motivo / nota {accion.tipo === "Rechazada" ? "*" : "(opcional)"}</Label>
                <Textarea value={accion.motivo} onChange={(e) => setAccion({ ...accion, motivo: e.target.value })} placeholder={accion.tipo === "Aprobada" ? "Ej. Transferencia confirmada en cuenta CLABE ..." : "Ej. Comprobante no coincide / referencia inválida"} data-testid="accion-motivo" className="rounded-none border-[#0A0A0A]" />
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setAccion(null)} className="h-11 px-5 border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm">Cancelar</button>
            <button onClick={confirmar} disabled={saving || (accion?.tipo === "Rechazada" && !accion?.motivo?.trim())} data-testid="accion-confirmar"
              className={`btn-hard h-11 px-5 border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm inline-flex items-center gap-2 disabled:opacity-60 ${accion?.tipo === "Aprobada" ? "bg-[#FFCC00] text-[#0A0A0A]" : "bg-[#B91C1C] text-white"}`}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (accion?.tipo === "Aprobada" ? "Aprobar y acreditar" : "Rechazar")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de carga de CFDI */}
      <Dialog open={upload !== null} onOpenChange={(o) => { if (!o) setUpload(null); }}>
        <DialogContent className="rounded-none border-[#0A0A0A]" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-black tracking-tight">Subir Factura (CFDI)</DialogTitle>
            <DialogDescription>
              {upload && `Carga el PDF y el XML timbrados para "${upload.recarga.empresa_nombre}". El comprobante quedará Timbrado y el tenant podrá descargarlos.`}
            </DialogDescription>
          </DialogHeader>
          {upload && (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B] flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> Archivo PDF</Label>
                <input type="file" accept="application/pdf,.pdf" data-testid="upload-pdf"
                  onChange={(e) => setUpload({ ...upload, pdf: e.target.files?.[0] || null })}
                  className="block w-full text-sm border border-[#0A0A0A] file:mr-3 file:border-0 file:bg-[#0A0A0A] file:text-white file:px-3 file:py-2 file:font-bold file:uppercase file:text-xs" />
                {upload.pdf && <p className="text-xs text-[#52525B] truncate">{upload.pdf.name}</p>}
              </div>
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B] flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> Archivo XML</Label>
                <input type="file" accept="application/xml,text/xml,.xml" data-testid="upload-xml"
                  onChange={(e) => setUpload({ ...upload, xml: e.target.files?.[0] || null })}
                  className="block w-full text-sm border border-[#0A0A0A] file:mr-3 file:border-0 file:bg-[#0A0A0A] file:text-white file:px-3 file:py-2 file:font-bold file:uppercase file:text-xs" />
                {upload.xml && <p className="text-xs text-[#52525B] truncate">{upload.xml.name}</p>}
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setUpload(null)} className="h-11 px-5 border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm">Cancelar</button>
            <button onClick={subirFactura} disabled={uploading || !upload?.pdf || !upload?.xml} data-testid="upload-confirmar"
              className="btn-hard h-11 px-5 bg-[#FFCC00] text-[#0A0A0A] border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm inline-flex items-center gap-2 disabled:opacity-60">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Subir y timbrar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Marcar Error */}
      <Dialog open={errorModal !== null} onOpenChange={(o) => { if (!o) setErrorModal(null); }}>
        <DialogContent className="rounded-none border-[#0A0A0A]" data-testid="error-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-black tracking-tight">Marcar solicitud con Error</DialogTitle>
            <DialogDescription>
              {errorModal && `Indica el motivo por el que no se puede timbrar la factura de "${errorModal.recarga.empresa_nombre}". El tenant lo verá y podrá corregir y reintentar.`}
            </DialogDescription>
          </DialogHeader>
          {errorModal && (
            <div className="space-y-2 py-2">
              <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Motivo del error *</Label>
              <Textarea value={errorModal.nota} onChange={(e) => setErrorModal({ ...errorModal, nota: e.target.value })}
                placeholder="Ej. Código Postal no coincide con el RFC" data-testid="error-nota" className="rounded-none border-[#0A0A0A] min-h-[90px]" />
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setErrorModal(null)} className="h-11 px-5 border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm">Cancelar</button>
            <button onClick={marcarError} disabled={savingError || !errorModal?.nota?.trim()} data-testid="error-confirmar"
              className="btn-hard h-11 px-5 bg-[#B91C1C] text-white border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm inline-flex items-center gap-2 disabled:opacity-60">
              {savingError ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />} Marcar Error
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
